import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AssessmentDetail from './AssessmentDetail';
import AssessmentsList from './AssessmentsList';

import Page from '@/components/Page';
import Stack from '@/components/Stack';
import Card from '@/components/Card';
import { Body, Heading } from '@/components/Typography';
import { Assessment, AssessmentsAPI } from '@/services/assessmentsAPI';

const AiAssessments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] =
    useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get externalId from URL query params
  const queryParams = new URLSearchParams(location.search);
  const externalIdFromUrl = queryParams.get('externalId');

  // Load assessments list and specific assessment if URL has externalId
  useEffect(() => {
    if (externalIdFromUrl) {
      // Load both list and specific assessment in parallel
      loadAssessments();
      loadSpecificAssessment(externalIdFromUrl);
    } else {
      // Just load the list
      loadAssessments();
    }
  }, []);

  // Handle external ID from URL after assessments list is loaded
  useEffect(() => {
    if (externalIdFromUrl && assessments.length > 0 && !selectedAssessment) {
      const assessment = assessments.find(
        (a) => a.external_id === externalIdFromUrl,
      );
      if (assessment) {
        setSelectedAssessment(assessment);
      }
    }
  }, [externalIdFromUrl, assessments]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AssessmentsAPI.getAssessments({
        include_reviewed: false,
        status: 'completed',
      });
      setAssessments(response.data);
    } catch (err) {
      setError('Failed to load assessments');
      console.error('Error loading assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificAssessment = async (externalId: string) => {
    try {
      const response = await AssessmentsAPI.getAssessment(externalId);
      // Create a basic Assessment object from the detailed response
      const basicAssessment: Assessment = {
        external_id: response.data.external_id,
        user_id: response.data.user_id,
        skill_id: response.data.skill_id,
        status: response.data.status,
        language: response.data.language,
        created_at: response.data.created_at,
        reviewed_at: response.data.reviewed_at,
      };
      setSelectedAssessment(basicAssessment);
    } catch (err) {
      console.error('Error loading specific assessment:', err);
      // Don't set error state here, let the list load
    }
  };

  const handleSelectAssessment = (assessment: Assessment) => {
    setSelectedAssessment(assessment);

    // Update URL with external ID
    const newUrl = `${location.pathname}?externalId=${assessment.external_id}`;
    navigate(newUrl);
  };

  const handleReviewSuccess = () => {
    // Reload assessments list after review
    loadAssessments();
    setSelectedAssessment(null);
    navigate(location.pathname);
  };

  return (
    <Page title="AI Assessments">
      <Stack
        gap={24}
        css={{ minHeight: '600px' }}
        style={{ alignItems: 'flex-start' }}
      >
        <div style={{ flex: '0 0 400px' }}>
          <AssessmentsList
            assessments={assessments}
            selectedAssessment={selectedAssessment}
            onSelectAssessment={handleSelectAssessment}
            loading={loading}
            error={error}
            onRefresh={loadAssessments}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Card changeStyle={{ padding: 24 }}>
            {selectedAssessment ? (
              <AssessmentDetail
                assessment={selectedAssessment}
                onReviewSuccess={handleReviewSuccess}
              />
            ) : (
              <Stack
                vertical
                align="center"
                justify="center"
                css={{ minHeight: '400px' }}
              >
                <Heading>No Assessment Selected</Heading>
                <Body>Select an assessment from the list to view details</Body>
              </Stack>
            )}
          </Card>
        </div>
      </Stack>
    </Page>
  );
};

export default AiAssessments;
